import React, { useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Parallax } from 'react-scroll-parallax';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    Stack,
    Typography,
    Avatar,
    useTheme,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    CardActions,
    alpha,
    Fade,
    Paper,
    Divider,
    Tooltip
} from '@mui/material';
import {
    Code,
    Analytics,
    Cloud,
    Timeline,
    Work,
    VerifiedUser,
    People,
    BusinessCenter,
    ArrowRightAlt,
    ChevronRight,
    CheckCircle,
    PlayCircle,
    ArrowForward,
    TrendingFlat,
    RocketLaunch,
    Star,
    FormatQuote,
    Circle,
    Close,
    StarHalf,
    StarBorder,
    Lightbulb,
    Speed,
    LocalOffer,
    EmojiEvents
} from '@mui/icons-material';

import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';

import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';


import crmImage from '../assets/images/crm.webp';
import analyticsImage from '../assets/images/ai-predictive-analytics.png';
import printflowImage from '../assets/images/printflow.webp';
import eduerp from '../assets/images/eduerp.png';
import manufacturingImage from '../assets/images/manufacturing.png';
import hrmImage from '../assets/images/hrm.jpg';
import solarImage from '../assets/images/solar.jpg';
// import heroImage from '../assets/images/product-dashboards-reports_thumbnail-1024x538.jpg';
import HeroSection from './home/home-hero';
import dighewarPetrol from '../assets/client-logo/Dighewar_petrol_pump.png';
import maliyeHospital from '../assets/client-logo/Maliye_hospital.png';
import shreeji from '../assets/client-logo/Shreeji.png';
import tanmayPrinting from '../assets/client-logo/Tanmay_printing.png';


const colors = {
    primary: '#3A56E8',
    secondary: '#6C63FF',
    accent: '#FF6584',
    dark: '#1A202C',
    light: '#F7FAFC',
    background: '#FFFFFF',
    textPrimary: '#2D3748',
    textSecondary: '#4A5568',
    success: '#48BB78',
    warning: '#ED8936',
    error: '#F56565',
    info: '#4299E1'
};

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const Home = ({ mode }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [openDialog, setOpenDialog] = React.useState(false);
    const [selectedVideo, setSelectedVideo] = React.useState('');

    // Hero section animation
    const heroVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                staggerChildren: 0.2,
                when: "beforeChildren"
            }
        }
    };

    // Enhanced Services data
    const services = [
        {
            title: 'Custom Software Development',
            description: 'Tailored solutions designed to meet your specific business needs with scalable architecture and modern technologies.',
            icon: <Code fontSize="large" />,
            image: 'https://source.unsplash.com/random/600x400/?software,development',
            tags: ['Web', 'Mobile', 'API', 'QA'],
            link: '/services/software-development',
            results: [
                'Increased operational efficiency',
                'Reduced development costs',
                'Improved customer experience',
                'Competitive advantage'
            ],
            color: 'primary'
        },
        {
            title: 'AI & Machine Learning',
            description: 'Intelligent applications that learn and improve with data, providing valuable insights and automation capabilities.',
            icon: <Analytics fontSize="large" />,
            image: 'https://source.unsplash.com/random/600x400/?ai,machinelearning',
            tags: ['Predictive', 'NLP', 'Vision', 'ML'],
            link: '/services/ai-ml',
            results: [
                'Data-driven decision making',
                'Process automation',
                'Enhanced customer insights',
                'Fraud detection & prevention'
            ],
            color: 'secondary'
        },
        {
            title: 'Cloud Solutions',
            description: 'Scalable and secure cloud infrastructure design, implementation, and management for optimal performance.',
            icon: <Cloud fontSize="large" />,
            image: 'https://source.unsplash.com/random/600x400/?cloud,computing',
            tags: ['AWS', 'Azure', 'GCP', 'DevOps'],
            link: '/services/cloud',
            results: [
                'Improved scalability',
                'Enhanced security',
                'Reduced infrastructure costs',
                'Increased business agility'
            ],
            color: 'info'
        },
        {
            title: 'Data Analytics',
            description: 'Transform your data into actionable insights with our advanced analytics solutions.',
            icon: <Timeline fontSize="large" />,
            image: 'https://source.unsplash.com/random/600x400/?data,analytics',
            tags: ['BI', 'Visualization', 'Big Data', 'ETL'],
            link: '/services/data-analytics',
            results: [
                'Improved decision making',
                'Enhanced operational visibility',
                'Increased revenue opportunities',
                'Better customer understanding'
            ],
            color: 'success'
        },
        {
            title: 'Cybersecurity',
            description: 'Comprehensive security solutions to protect your digital assets and infrastructure.',
            icon: <VerifiedUser fontSize="large" />,
            image: 'https://source.unsplash.com/random/600x400/?cybersecurity',
            tags: ['Security', 'Compliance', 'Risk', 'SOC'],
            link: '/services/cybersecurity',
            results: [
                'Reduced security risks',
                'Regulatory compliance',
                'Protected customer data',
                'Business continuity'
            ],
            color: 'warning'
        },
        {
            title: 'UI/UX Design',
            description: 'Beautiful, intuitive interfaces that enhance user engagement and satisfaction.',
            icon: <Work fontSize="large" />,
            image: 'https://source.unsplash.com/random/600x400/?design,ui',
            tags: ['Research', 'Wireframes', 'Prototyping', 'Testing'],
            link: '/services/ui-ux',
            results: [
                'Improved user satisfaction',
                'Higher conversion rates',
                'Reduced development time',
                'Consistent brand experience'
            ],
            color: 'error'
        }
    ];

    // Portfolio projects
    // const projects = [
    //     {
    //         title: 'Enterprise CRM Suite',
    //         description: 'Custom CRM system with AI-powered analytics for a Fortune 500 company, streamlining sales and customer management.',
    //         image: crmImage,
    //         tags: ['React', 'Node.js', 'MongoDB', 'AI', 'Salesforce'],
    //         link: '/case-studies/crm-system',
    //         results: [
    //             '+45% sales team productivity',
    //             '60% reduction in response time',
    //             '75% improvement in data accuracy',
    //             '300+ enterprise users onboarded'
    //         ],
    //         rating: '4.9',
    //         accentColor: '#3f51b5',
    //         secondaryColor: '#2196f3'
    //     },
    //     {
    //         title: 'AI Analytics Platform',
    //         description: 'Advanced analytics dashboard with predictive capabilities for fintech transactions and risk assessment.',
    //         image: analyticsImage,
    //         tags: ['Python', 'TensorFlow', 'AWS', 'Data Visualization'],
    //         link: '/case-studies/analytics-platform',
    //         results: [
    //             '80% reduction in fraud cases',
    //             '92% prediction accuracy',
    //             '10M+ monthly transactions processed',
    //             'Real-time risk scoring'
    //         ],
    //         rating: '4.8',
    //         accentColor: '#ff5722',
    //         secondaryColor: '#ff9800'
    //     },
    //     {
    //         title: 'EduERP - Education Management',
    //         description: 'Comprehensive ERP solution for universities handling admissions, scheduling, and student lifecycle management.',
    //         image: eduerp,
    //         tags: ['Java', 'Spring Boot', 'PostgreSQL', 'Microservices'],
    //         link: '/case-studies/education-erp',
    //         results: [
    //             '90% paperless administration',
    //             '50+ institutions implemented',
    //             'Centralized student database',
    //             'Mobile app for students/parents'
    //         ],
    //         rating: '4.7',
    //         accentColor: '#009688',
    //         secondaryColor: '#4caf50'
    //     },
    //     {
    //         title: 'PrintFlow Pro',
    //         description: 'End-to-end workflow management system for large-scale printing operations with automated quality checks.',
    //         image: printflowImage,
    //         tags: ['C#', '.NET Core', 'Computer Vision', 'IoT'],
    //         link: '/case-studies/printing-solution',
    //         results: [
    //             '40% faster job turnaround',
    //             '30% material waste reduction',
    //             'Automated proofing system',
    //             'Real-time production tracking'
    //         ],
    //         rating: '4.6',
    //         accentColor: '#673ab7',
    //         secondaryColor: '#9c27b0'
    //     }
    // ];

    const projects = [
        {
            title: 'Enterprise CRM Suite',
            description: 'Custom CRM system with AI-powered analytics for a Fortune 500 company, streamlining sales and customer management.',
            image: crmImage,
            tags: ['React', 'Node.js', 'MongoDB', 'AI', 'Salesforce'],
            link: '/case-studies/crm-system',
            results: [
                '+45% sales team productivity',
                '60% reduction in response time',
                '75% improvement in data accuracy',
                '300+ enterprise users onboarded'
            ],
            rating: '4.9',
            accentColor: '#3f51b5',
            secondaryColor: '#2196f3'
        },
        // {
        //     title: 'AI Analytics Platform',
        //     description: 'Advanced analytics dashboard with predictive capabilities for fintech transactions and risk assessment.',
        //     image: analyticsImage,
        //     tags: ['Python', 'TensorFlow', 'AWS', 'Data Visualization'],
        //     link: '/case-studies/analytics-platform',
        //     results: [
        //         '80% reduction in fraud cases',
        //         '92% prediction accuracy',
        //         '10M+ monthly transactions processed',
        //         'Real-time risk scoring'
        //     ],
        //     rating: '4.8',
        //     accentColor: '#ff5722',
        //     secondaryColor: '#ff9800'
        // },

        {
            title: 'PrintFlow Pro',
            description: 'End-to-end workflow management system for large-scale printing operations with automated quality checks.',
            image: printflowImage,
            tags: ['C#', '.NET Core', 'Computer Vision', 'IoT'],
            link: '/case-studies/printing-solution',
            results: [
                '40% faster job turnaround',
                '30% material waste reduction',
                'Automated proofing system',
                'Real-time production tracking'
            ],
            rating: '4.6',
            accentColor: '#673ab7',
            secondaryColor: '#9c27b0'
        },
        {
            title: 'SmartHRM - Human Resource Management',
            description: 'Modern HRM platform enabling automated payroll, attendance tracking, performance reviews, and onboarding for large enterprises.',
            image: hrmImage,
            tags: ['Angular', 'Spring Boot', 'MySQL', 'Face Recognition', 'Payroll Engine'],
            link: '/case-studies/hrm-solution',
            results: [
                '85% improvement in payroll accuracy',
                '70% faster onboarding',
                '99.5% attendance logging accuracy',
                'Compliant with global labor laws'
            ],
            rating: '4.8',
            accentColor: '#e91e63',
            secondaryColor: '#f06292'
        },
        {
            title: 'ManuCore - Production Management System',
            description: 'Comprehensive manufacturing ERP with production planning, machine monitoring, inventory, and quality control.',
            image: manufacturingImage,
            tags: ['React', 'Express', 'Node.js', 'PostgreSQL', 'IIoT', 'AWS'],
            link: '/case-studies/manufacturing-erp',
            results: [
                '30% increase in production efficiency',
                'Real-time machine & line status',
                'Integrated quality checkpoints',
                'Reduced downtime by 50%'
            ],
            rating: '4.9',
            accentColor: '#607d8b',
            secondaryColor: '#455a64'
        },
        {
            title: 'SolarOne - Solar Installation Service Suite',
            description: 'Full-stack platform for managing solar panel design, installation, maintenance scheduling, and customer lifecycle.',
            image: solarImage,
            tags: ['React', 'Node.js', 'Mapbox', 'Stripe', 'IoT Sensors'],
            link: '/case-studies/solar-service-suite',
            results: [
                '50% faster quote-to-installation time',
                'Automated solar panel layout planner',
                'Live performance monitoring',
                'Integrated CRM and billing'
            ],
            rating: '4.7',
            accentColor: '#ffc107',
            secondaryColor: '#ffeb3b'
        },
        {
            title: 'EduERP - Education Management',
            description: 'Comprehensive ERP solution for universities handling admissions, scheduling, and student lifecycle management.',
            image: eduerp,
            tags: ['Java', 'Spring Boot', 'MySQL', 'Microservices'],
            link: '/case-studies/education-erp',
            results: [
                '90% paperless administration',
                '50+ institutions implemented',
                'Centralized student database',
                'Mobile app for students/parents'
            ],
            rating: '4.7',
            accentColor: '#009688',
            secondaryColor: '#4caf50'
        },
    ];


    const statsData = [
        { icon: <Speed />, value: 98, suffix: '%', label: 'Customer Satisfaction' },
        { icon: <Lightbulb />, value: 200, suffix: '+', label: 'Solutions Delivered' },
        { icon: <LocalOffer />, value: 5, suffix: '+', label: 'Years of Excellence' },
        { icon: <EmojiEvents />, value: 18, suffix: '', label: 'Industry Awards' },
    ];

    // Company stats
    const companyStats = [
        { label: 'Years', value: '10+', icon: <Timeline />, description: 'Since 2013' },
        { label: 'Projects', value: '250+', icon: <Work />, description: 'Completed' },
        { label: 'Clients', value: '200+', icon: <VerifiedUser />, description: '95% retention' },
        { label: 'Team', value: '50+', icon: <People />, description: 'Experts' },
    ];

    // Testimonials data
    const testimonials = [
        {
            name: 'ShreeJi Industries',
            // role: 'CTO, TechCorp',
            // avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
            rating: 4.7,
            comment: "We struggled with tracking inventory and production delays. Soranova's software streamlined our workflow and gave real-time visibility. Now, we meet delivery deadlines with zero manual errors.",
            companyLogo: shreeji
        },
        {
            name: 'Dighewar Petrolium',
            // role: 'Product Director, Finova',
            // avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
            rating: 4.8,
            comment: "We faced daily issues with fuel stock tracking and billing. Soranova automated our entire system, from entry to settlement. Now, operations run faster with complete accuracy.",
            companyLogo: dighewarPetrol
        },
        {
            name: 'Maliye Hospitals',
            // role: 'CEO, EduTech Solutions',
            // avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
            rating: 5,
            comment: "Our front desk was overwhelmed and patient care was getting delayed.Soranova’s system brought order to the chaos with smart scheduling and EMR.Now, we focus more on healing than handling paperwork.",
            companyLogo: maliyeHospital
        },
        {
            name: 'Tanmay Printings',
            // role: 'Head of Engineering, HealthTrack',
            // avatar: 'https://randomuser.me/api/portraits/men/75.jpg',
            rating: 4.6,
            comment: "Running multiple press sites meant missed updates and confusion. Soranova gave us real-time control over everything from one screen. It's like going from typewriters to tablets—game changer!",
            companyLogo: tanmayPrinting
        }
    ];

    const renderRatingStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 1; i <= 5; i++) {
            if (i <= fullStars) {
                stars.push(<Star key={i} sx={{ color: colors.warning, fontSize: '1.2rem' }} />);
            } else if (i === fullStars + 1 && hasHalfStar) {
                stars.push(<StarHalf key={i} sx={{ color: colors.warning, fontSize: '1.2rem' }} />);
            } else {
                stars.push(<StarBorder key={i} sx={{ color: colors.warning, fontSize: '1.2rem' }} />);
            }
        }

        return stars;
    };



    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 2,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 6000,
        pauseOnHover: true,
        responsive: [
            {
                breakpoint: 900,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    };



    const particlesInit = useCallback(async (engine) => {
        try {
            await loadSlim(engine);
        } catch (error) {
            console.error("Error loading tsparticles:", error);
        }
    }, []);

    const particlesOptions = {
        fpsLimit: 60,
        particles: {
            number: {
                value: isMobile ? 40 : 80,
                density: {
                    enable: true,
                    area: 800
                }
            },
            color: {
                value: theme.palette.primary.main
            },
            shape: {
                type: "circle"
            },
            opacity: {
                value: 0.5,
                random: true
            },
            size: {
                value: 3,
                random: true
            },
            move: {
                enable: true,
                speed: 2,
                direction: "none",
                random: true,
                straight: false,
                outModes: "out"
            },
            links: {
                enable: true,
                distance: 150,
                color: theme.palette.primary.main,
                opacity: 0.2,
                width: 1
            }
        },
        interactivity: {
            detectsOn: "window",
            events: {
                onHover: {
                    enable: true,
                    mode: "grab"
                },
                onClick: {
                    enable: true,
                    mode: "push"
                }
            },
            modes: {
                grab: {
                    distance: 140,
                    links: {
                        opacity: 0.5
                    }
                },
                push: {
                    quantity: 4
                }
            }
        },
        detectRetina: true
    };

    const handleVideoOpen = (videoUrl) => {
        setSelectedVideo(videoUrl);
        setOpenDialog(true);
    };

    const handleVideoClose = () => {
        setOpenDialog(false);
        setSelectedVideo('');
    };

    const getColorValue = (colorName) => {
        switch (colorName) {
            case 'primary': return theme.palette.primary.main;
            case 'secondary': return theme.palette.secondary.main;
            case 'info': return theme.palette.info.main;
            case 'success': return theme.palette.success.main;
            case 'warning': return theme.palette.warning.main;
            case 'error': return theme.palette.error.main;
            default: return theme.palette.primary.main;
        }
    };



    return (
        <Box sx={{
            bgcolor: 'background.default',
            color: 'text.primary',
            minHeight: '100vh',
            overflowX: 'hidden',
        }}>

            <HeroSection mode={mode} />

            {/* <Box sx={{
                py: 10,
                bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'hidden',
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(to right, rgba(10,10,26,1) 0%, rgba(10,10,26,0) 10%, rgba(10,10,26,0) 90%, rgba(10,10,26,1) 100%)'
                        : 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 10%, rgba(255,255,255,0) 90%, rgba(255,255,255,1) 100%)',
                    zIndex: 2,
                    pointerEvents: 'none'
                }
            }}>
                <Container maxWidth="lg">
                    <Box sx={{
                        textAlign: 'center',
                        mb: 8,
                        position: 'relative',
                        zIndex: 3
                    }}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            <Chip
                                label="TRUSTED BY"
                                color="primary"
                                sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    letterSpacing: 1,
                                    px: 3,
                                    py: 1,
                                    fontSize: '0.75rem',
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                }}
                            />
                            <Typography
                                variant="h3"
                                component="h2"
                                sx={{
                                    fontWeight: 800,
                                    mb: 3,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    fontSize: { xs: '2rem', md: '2.5rem' }
                                }}
                            >
                                Industry Leaders
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                color="text.secondary"
                                sx={{
                                    maxWidth: 700,
                                    mx: 'auto',
                                    fontSize: { xs: '1rem', md: '1.1rem' }
                                }}
                            >
                                Partnering with innovative companies to deliver exceptional results
                            </Typography>
                        </motion.div>
                    </Box>


                    <Box sx={{
                        display: { xs: 'none', md: 'block' },
                        position: 'relative',
                        overflow: 'hidden',
                        py: 3,
                        '&:hover .client-scroll': {
                            animationPlayState: 'paused'
                        }
                    }}>
                        <Box sx={{
                            display: 'flex',
                            width: 'max-content',
                            animation: 'scroll 40s linear infinite',
                            '@keyframes scroll': {
                                '0%': { transform: 'translateX(0)' },
                                '100%': { transform: 'translateX(-50%)' }
                            }
                        }} className="client-scroll">
                            {[
                                dighewarPetrol,
                                maliyeHospital,
                                shreeji,
                                tanmayPrinting
                            ].map((logo, i) => (
                                <Box key={`client-${i}`} sx={{
                                    px: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    width: 200,
                                    height: 80,
                                }}>
                                    <Box
                                        component="img"
                                        src={logo}
                                        alt={`Client logo ${i + 1}`}
                                        sx={{
                                            height: 'auto',
                                            maxHeight: 200,
                                            width: 'auto',
                                            maxWidth: 300,
                                            filter: theme.palette.mode === 'dark' && !logo.includes('black')
                                                ? 'brightness(0) invert(1)'
                                                : 'none',
                                            opacity: 0.9,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                opacity: 1,
                                                transform: 'scale(1.1)'
                                            },
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{
                        display: { xs: 'grid', md: 'none' },
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 4,
                        mt: 4
                    }}>
                        {[
                            dighewarPetrol,
                            maliyeHospital,
                            shreeji,
                            tanmayPrinting
                        ].map((logo, i) => (
                            <Box key={`client-mobile-${i}`} sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 80,
                                p: 2,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                borderRadius: 2
                            }}>
                                <Box
                                    component="img"
                                    src={logo}
                                    alt={`Client logo ${i + 1}`}
                                    sx={{
                                        height: 'auto',
                                        maxHeight: 30,
                                        width: 'auto',
                                        maxWidth: 120,
                                        filter: theme.palette.mode === 'dark' && !logo.includes('black')
                                            ? 'brightness(0) invert(1)'
                                            : 'none',
                                        opacity: 0.9,
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Container>
            </Box> */}

            {/* Services Section */}
            <Box id="services" sx={{
                py: { xs: 10, md: 16 },
                bgcolor: alpha(colors.primary, 0.05),
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Enhanced background gradient with smoother transition */}
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '40%',
                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                    zIndex: 0,
                    clipPath: 'polygon(0 0, 100% 0, 100% 75%, 0 100%)',
                    opacity: 0.85
                }} />

                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Fade in={true} timeout={1000}>
                        <Box textAlign="center" mb={10}>
                            <Typography
                                variant="overline"
                                color="common.white"
                                fontWeight="bold"
                                sx={{
                                    letterSpacing: 3,
                                    fontSize: '0.9rem',
                                    mb: 2,
                                    display: 'inline-block',
                                    backgroundColor: alpha(colors.primary, 0.2),
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 1
                                }}
                            >
                                OUR SERVICES
                            </Typography>
                            <Typography
                                component="h2"
                                variant="h2"
                                color="common.white"
                                gutterBottom
                                fontWeight="bold"
                                sx={{
                                    mb: 3,
                                    fontSize: { xs: '2.2rem', sm: '2.5rem', md: '3rem' }
                                }}
                            >
                                Tailored Technology Services
                            </Typography>
                            <Typography
                                variant="h6"
                                color={alpha('#ffffff', 0.95)}
                                paragraph
                                sx={{
                                    maxWidth: 800,
                                    mx: 'auto',
                                    fontSize: { xs: '1.1rem', md: '1.2rem' },
                                    lineHeight: 1.7,
                                    fontWeight: 400
                                }}
                            >
                                Expert consulting and implementation services to complement our product offerings.
                            </Typography>
                        </Box>
                    </Fade>

                    {/* Top Row - First 3 Services */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)'
                        },
                        gap: { xs: 4, md: 5 },
                        mb: { xs: 8, md: 12 }
                    }}>
                        {services.slice(0, 3).map((service, index) => (
                            <Fade
                                key={`service-top-${index}`}
                                in={true}
                                timeout={800 + (index * 200)}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        minHeight: { xs: '320px', md: '360px' },
                                        maxHeight: 'auto',
                                        transition: 'all 0.4s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: `0 15px 30px -10px ${alpha(getColorValue(service.color), 0.15)}`,
                                            borderLeft: `5px solid ${getColorValue(service.color)}`
                                        },
                                        borderRadius: '16px',
                                        borderLeft: '5px solid transparent',
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${alpha(colors.primary, 0.08)}`,
                                        p: 4,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* Subtle background accent */}
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: '150px',
                                        height: '150px',
                                        backgroundColor: alpha(getColorValue(service.color), 0.03),
                                        borderBottomLeftRadius: '100%',
                                        zIndex: 0
                                    }} />

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: getColorValue(service.color),
                                            color: 'white',
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            mb: 3,
                                            boxShadow: `0 8px 20px ${alpha(getColorValue(service.color), 0.25)}`,
                                            flexShrink: 0,
                                            position: 'relative',
                                            zIndex: 1
                                        }}
                                    >
                                        {service.icon}
                                    </Box>

                                    <Box sx={{ flex: '1 0 auto', mb: 3, position: 'relative', zIndex: 1 }}>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            gutterBottom
                                            fontWeight="bold"
                                            color={theme.palette.text.primary}
                                            sx={{ mb: 2 }}
                                        >
                                            {service.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            component="p"
                                            gutterBottom
                                            color={theme.palette.text.primary}
                                            sx={{ mb: 2 }}
                                        >
                                            {service.description}
                                        </Typography>

                                        <List dense sx={{ pl: 0 }}>
                                            {service.results.slice(0, 3).map((result, idx) => (
                                                <ListItem key={`result-top-${index}-${idx}`} disableGutters sx={{ py: 0.5 }}>
                                                    <ListItemIcon sx={{ minWidth: '36px' }}>
                                                        <CheckCircle sx={{ color: getColorValue(service.color), fontSize: '1.1rem' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={result}
                                                        primaryTypographyProps={{
                                                            color: theme.palette.text.primary,
                                                            variant: 'body1',
                                                            fontWeight: '500'
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>

                                    {/* <Button
                                        variant="text"
                                        size="medium"
                                        endIcon={<ArrowForward />}
                                        sx={{
                                            mt: 'auto',
                                            alignSelf: 'flex-start',
                                            color: getColorValue(service.color),
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: alpha(getColorValue(service.color), 0.08),
                                                transform: 'translateX(4px)'
                                            },
                                            transition: 'all 0.3s',
                                            zIndex: 1
                                        }}
                                        onClick={() => navigate(service.link)}
                                    >
                                        Learn more
                                    </Button> */}
                                </Paper>
                            </Fade>
                        ))}
                    </Box>

                    {/* Bottom Row - Last 3 Services */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)'
                        },
                        gap: { xs: 4, md: 5 },
                        mt: { xs: 2, md: 4 }
                    }}>
                        {services.slice(3, 6).map((service, index) => (
                            <Fade
                                key={`service-bottom-${index}`}
                                in={true}
                                timeout={1000 + (index * 200)}
                                style={{ transitionDelay: `${300 + (index * 100)}ms` }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        minHeight: { xs: '320px', md: '360px' },
                                        maxHeight: 'auto',
                                        transition: 'all 0.4s ease',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: `0 15px 30px -10px ${alpha(getColorValue(service.color), 0.15)}`,
                                            borderLeft: `5px solid ${getColorValue(service.color)}`
                                        },
                                        borderRadius: '16px',
                                        borderLeft: '5px solid transparent',
                                        backgroundColor: theme.palette.background.paper,
                                        border: `1px solid ${alpha(colors.primary, 0.08)}`,
                                        p: 4,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {/* Subtle background accent */}
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        width: '150px',
                                        height: '150px',
                                        backgroundColor: alpha(getColorValue(service.color), 0.03),
                                        borderBottomLeftRadius: '100%',
                                        zIndex: 0
                                    }} />

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: getColorValue(service.color),
                                            color: 'white',
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            mb: 3,
                                            boxShadow: `0 8px 20px ${alpha(getColorValue(service.color), 0.25)}`,
                                            flexShrink: 0,
                                            position: 'relative',
                                            zIndex: 1
                                        }}
                                    >
                                        {service.icon}
                                    </Box>

                                    <Box sx={{ flex: '1 0 auto', mb: 3, position: 'relative', zIndex: 1 }}>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            gutterBottom
                                            fontWeight="bold"
                                            color={theme.palette.text.primary}
                                            sx={{ mb: 2 }}
                                        >
                                            {service.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            component="p"
                                            gutterBottom
                                            color={theme.palette.text.primary}
                                            sx={{ mb: 2 }}
                                        >
                                            {service.description}
                                        </Typography>

                                        <List dense sx={{ pl: 0 }}>
                                            {service.results.slice(0, 3).map((result, idx) => (
                                                <ListItem key={`result-bottom-${index}-${idx}`} disableGutters sx={{ py: 0.5 }}>
                                                    <ListItemIcon sx={{ minWidth: '36px' }}>
                                                        <CheckCircle sx={{ color: getColorValue(service.color), fontSize: '1.1rem' }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={result}
                                                        primaryTypographyProps={{
                                                            color: theme.palette.text.primary,
                                                            variant: 'body1',
                                                            fontWeight: '500'
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>

                                    {/* <Button
                                        variant="text"
                                        size="medium"
                                        endIcon={<ArrowForward />}
                                        sx={{
                                            mt: 'auto',
                                            alignSelf: 'flex-start',
                                            color: getColorValue(service.color),
                                            fontWeight: 600,
                                            '&:hover': {
                                                backgroundColor: alpha(getColorValue(service.color), 0.08),
                                                transform: 'translateX(4px)'
                                            },
                                            transition: 'all 0.3s',
                                            zIndex: 1
                                        }}
                                        onClick={() => navigate(service.link)}
                                    >
                                        Learn more
                                    </Button> */}
                                </Paper>
                            </Fade>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* Stats Section */}
            {/* <Box sx={{
                py: { xs: 8, md: 12 },
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                color: '#fff',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23ffffff\' fill-opacity=\'0.08\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
                    opacity: 0.3,
                },
            }}>
                <Container maxWidth="lg">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                    >
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(4, 1fr)'
                            },
                            gap: { xs: 4, md: 5 },
                            mt: { xs: 2, md: 4 }
                        }}>
                            {companyStats.map((stat, index) => (
                                <motion.div
                                    key={`stat-${index}`}
                                    variants={fadeInUp}
                                    whileHover={{ y: -5 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Box sx={{
                                        textAlign: 'center',
                                        p: 3,
                                        position: 'relative',
                                        zIndex: 1,
                                        '&:hover': {
                                            '& .stat-glow': {
                                                opacity: 0.8,
                                            }
                                        }
                                    }}>
                                        <Box className="stat-glow" sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '50%',
                                            background: `radial-gradient(circle at center, ${theme.palette.primary.light} 0%, transparent 70%)`,
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease',
                                            zIndex: -1,
                                        }} />

                                        <Avatar
                                            sx={{
                                                width: isMobile ? 70 : 90,
                                                height: isMobile ? 70 : 90,
                                                bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                mb: 3,
                                                mx: 'auto',
                                                backdropFilter: 'blur(8px)',
                                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                                boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                                }
                                            }}
                                        >
                                            {React.cloneElement(stat.icon, {
                                                sx: {
                                                    fontSize: isMobile ? 36 : 46,
                                                    color: '#fff',
                                                }
                                            })}
                                        </Avatar>

                                        <Typography
                                            variant={isMobile ? 'h4' : 'h3'}
                                            component="div"
                                            sx={{
                                                fontWeight: 800,
                                                mb: 1,
                                                background: 'linear-gradient(90deg, #fff, rgba(255,255,255,0.8))',
                                                WebkitBackgroundClip: 'text',
                                                backgroundClip: 'text',
                                                color: 'transparent',
                                                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>

                                        <Typography
                                            variant="h6"
                                            component="div"
                                            sx={{
                                                fontWeight: 700,
                                                mb: 1.5,
                                                color: theme.palette.secondary.light
                                            }}
                                        >
                                            {stat.label}
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            sx={{
                                                opacity: 0.9,
                                                fontWeight: 500,
                                                maxWidth: '80%',
                                                mx: 'auto',
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {stat.description}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>
                    </motion.div>
                </Container>
            </Box> */}



            {/* Portfolio Section */}
            <Box id="portfolio" sx={{
                py: { xs: 10, md: 15 },
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(180deg, #0A0A0F 0%, #1A1A2E 100%)'
                    : 'linear-gradient(180deg, #F9FAFF 0%, #E6F0FF 100%)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -300,
                    right: -300,
                    width: 800,
                    height: 800,
                    borderRadius: '50%',
                    background: theme.palette.mode === 'dark'
                        ? `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%)`
                        : `radial-gradient(circle, ${alpha(theme.palette.secondary.light, 0.2)} 0%, transparent 70%)`,
                    zIndex: 0,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -400,
                    left: -400,
                    width: 800,
                    height: 800,
                    borderRadius: '50%',
                    background: theme.palette.mode === 'dark'
                        ? `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 70%)`
                        : `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.15)} 0%, transparent 70%)`,
                    zIndex: 0,
                }
            }}>
                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Animated Decorative Elements */}
                    <Box sx={{
                        position: 'absolute',
                        top: 100,
                        left: -50,
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`,
                        filter: 'blur(40px)',
                        opacity: 0.3,
                        animation: 'float 8s ease-in-out infinite',
                        '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0) translateX(0)' },
                            '50%': { transform: 'translateY(-20px) translateX(20px)' }
                        }
                    }} />

                    <Box sx={{
                        position: 'absolute',
                        bottom: 50,
                        right: -80,
                        width: 160,
                        height: 160,
                        borderRadius: '50%',
                        background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.success.main})`,
                        filter: 'blur(50px)',
                        opacity: 0.2,
                        animation: 'pulse 12s ease-in-out infinite',
                        '@keyframes pulse': {
                            '0%, 100%': { transform: 'scale(1)' },
                            '50%': { transform: 'scale(1.2)' }
                        }
                    }} />

                    {/* Header Section */}
                    <Box sx={{
                        textAlign: 'center',
                        mb: { xs: 8, md: 12 },
                        position: 'relative'
                    }}>
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.3 }}
                            variants={{
                                hidden: { opacity: 0, y: 30 },
                                visible: {
                                    opacity: 1,
                                    y: 0,
                                    transition: {
                                        duration: 0.8,
                                        ease: [0.16, 1, 0.3, 1]
                                    }
                                }
                            }}
                        >
                            <Chip
                                label="PORTFOLIO SHOWCASE"
                                color="primary"
                                sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    letterSpacing: 2,
                                    px: 3,
                                    py: 1,
                                    fontSize: '0.75rem',
                                    backdropFilter: 'blur(10px)',
                                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    color: theme.palette.primary.main,
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    textTransform: 'uppercase'
                                }}
                            />
                            <Typography
                                variant={isMobile ? 'h3' : 'h1'}
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontWeight: 800,
                                    letterSpacing: '-0.03em',
                                    background: theme.palette.mode === 'dark'
                                        ? `linear-gradient(180deg, #FFF 0%, ${theme.palette.secondary.light} 100%)`
                                        : `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 2,
                                    lineHeight: 1.2
                                }}
                            >
                                Our Masterpiece <Box component="span" sx={{ color: theme.palette.primary.main }}>Projects</Box>
                            </Typography>
                            <Typography
                                variant={isMobile ? 'body1' : 'h5'}
                                color="text.secondary"
                                sx={{
                                    maxWidth: 700,
                                    mx: 'auto',
                                    lineHeight: 1.6,
                                    fontSize: isMobile ? '1rem' : '1.25rem',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -20,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 80,
                                        height: 4,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        borderRadius: 2
                                    }
                                }}
                            >
                                Discover how we transform ideas into stunning digital experiences
                            </Typography>
                        </motion.div>
                    </Box>

                    {/* Projects Grid */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                        variants={{
                            hidden: { opacity: 0 },
                            visible: {
                                opacity: 1,
                                transition: {
                                    staggerChildren: 0.15,
                                    delayChildren: 0.3
                                }
                            }
                        }}
                    >
                        <Grid container spacing={{ xs: 4, md: 6 }} sx={{
                            justifyContent: 'center',
                            '& .MuiGrid-item': {
                                display: 'flex',
                                justifyContent: 'center'
                            }
                        }}>
                            {projects.map((project, index) => (
                                <Grid item xs={12} sm={6} md={6} lg={2} xl={2} key={`project-${index}`} sx={{
                                    maxWidth: 400
                                }}
                                >
                                    <motion.div
                                        variants={{
                                            hidden: { opacity: 0, y: 50 },
                                            visible: {
                                                opacity: 1,
                                                y: 0,
                                                transition: {
                                                    duration: 0.6,
                                                    ease: [0.16, 1, 0.3, 1]
                                                }
                                            }
                                        }}
                                        whileHover={{
                                            y: -10,
                                            transition: { duration: 0.3 }
                                        }}
                                        style={{ height: '100%' }}
                                    >
                                        <Card sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-10px)',
                                                boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.1)}`,
                                            },
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                            border: 'none',
                                            background: theme.palette.mode === 'dark'
                                                ? 'linear-gradient(145deg, #1E1E2E, #2A2A3A)'
                                                : 'linear-gradient(145deg, #FFFFFF, #F8FAFF)',
                                            boxShadow: theme.shadows[4],
                                            position: 'relative',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: 6,
                                                background: `linear-gradient(90deg, ${project.accentColor || theme.palette.primary.main}, ${project.secondaryColor || theme.palette.secondary.main})`
                                            }
                                        }}>
                                            {/* Project Image with Enhanced Hover Effect */}
                                            <Box sx={{
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: `radial-gradient(circle at center, ${alpha(project.accentColor || theme.palette.primary.main, 0.3)} 0%, transparent 70%)`,
                                                    opacity: 0,
                                                    transition: 'opacity 0.5s ease',
                                                    pointerEvents: 'none'
                                                },
                                                '&:hover::after': {
                                                    opacity: 0.6
                                                }
                                            }}>
                                                <Box
                                                    component="img"
                                                    src={project.image}
                                                    alt={project.title}
                                                    sx={{
                                                        width: '100%',
                                                        height: isMobile ? 240 : 300,
                                                        objectFit: 'cover',
                                                        transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                                                        '&:hover': {
                                                            transform: 'scale(1.08)'
                                                        }
                                                    }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 16,
                                                        right: 16,
                                                        zIndex: 2
                                                    }}
                                                >
                                                    <Chip
                                                        label={`0${index + 1}`}
                                                        sx={{
                                                            fontWeight: 800,
                                                            fontSize: '0.9rem',
                                                            bgcolor: alpha(project.accentColor || theme.palette.primary.main, 0.9),
                                                            color: '#FFF',
                                                            boxShadow: `0 4px 12px ${alpha(project.accentColor || theme.palette.primary.main, 0.3)}`
                                                        }}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Project Content */}
                                            <CardContent sx={{
                                                flexGrow: 1,
                                                px: { xs: 2.5, md: 3 },
                                                py: { xs: 2.5, md: 3 },
                                                position: 'relative'
                                            }}>
                                                {/* Floating Accent Dot */}
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: -10,
                                                    left: 20,
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: project.accentColor || theme.palette.primary.main,
                                                    boxShadow: `0 0 12px ${alpha(project.accentColor || theme.palette.primary.main, 0.8)}`
                                                }} />

                                                <Stack direction="row" spacing={1} sx={{
                                                    mb: 2,
                                                    flexWrap: 'wrap',
                                                    rowGap: 1,
                                                    '& .MuiChip-root': {
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: `0 4px 8px ${alpha('#000', 0.1)}`
                                                        }
                                                    }
                                                }}>
                                                    {project.tags.map((tag, i) => (
                                                        <Chip
                                                            key={`tag-${index}-${i}`}
                                                            label={tag}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: 'transparent',
                                                                fontWeight: 600,
                                                                border: `1px solid ${alpha(project.accentColor || theme.palette.primary.main, 0.3)}`,
                                                                color: theme.palette.text.secondary,
                                                                '& .MuiChip-label': {
                                                                    px: 1.2,
                                                                    fontSize: '0.7rem'
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Stack>

                                                <Typography
                                                    variant={isMobile ? 'h6' : 'h5'}
                                                    component="h3"
                                                    gutterBottom
                                                    sx={{
                                                        fontWeight: 800,
                                                        mb: 2,
                                                        lineHeight: 1.3,
                                                        color: theme.palette.text.primary,
                                                        position: 'relative',
                                                        display: 'inline-block',
                                                        '&::after': {
                                                            content: '""',
                                                            position: 'absolute',
                                                            bottom: -4,
                                                            left: 0,
                                                            width: 40,
                                                            height: 3,
                                                            background: `linear-gradient(90deg, ${project.accentColor || theme.palette.primary.main}, transparent)`,
                                                            borderRadius: 2
                                                        }
                                                    }}
                                                >
                                                    {project.title}
                                                </Typography>

                                                <Typography
                                                    variant="body1"
                                                    color="text.secondary"
                                                    paragraph
                                                    sx={{
                                                        mb: 2,
                                                        fontSize: isMobile ? '0.875rem' : '1rem',
                                                        lineHeight: 1.6
                                                    }}
                                                >
                                                    {project.description}
                                                </Typography>

                                                <List dense sx={{ mb: 2 }}>
                                                    {project.results.map((result, i) => (
                                                        <ListItem
                                                            key={`result-${index}-${i}`}
                                                            sx={{
                                                                px: 0,
                                                                py: 0.75,
                                                                alignItems: 'flex-start',
                                                                '&:hover': {
                                                                    '& .MuiListItemIcon-root': {
                                                                        transform: 'scale(1.2)'
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{
                                                                minWidth: 32,
                                                                mt: '4px',
                                                                transition: 'transform 0.3s ease',
                                                                color: project.accentColor || theme.palette.primary.main
                                                            }}>
                                                                <CheckCircle fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={result}
                                                                primaryTypographyProps={{
                                                                    variant: 'body2',
                                                                    color: 'text.primary'
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>

                                                <Box sx={{
                                                    mt: 'auto',
                                                    pt: 1,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        '& svg': {
                                                            color: project.accentColor || theme.palette.primary.main,
                                                            mr: 1
                                                        }
                                                    }}>
                                                        <Star fontSize="small" />
                                                        <Typography variant="body2" sx={{
                                                            fontWeight: 600,
                                                            background: theme.palette.mode === 'dark'
                                                                ? `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`
                                                                : `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                                                            WebkitBackgroundClip: 'text',
                                                            WebkitTextFillColor: 'transparent'
                                                        }}>
                                                            {project.rating || '5.0'} Rating
                                                        </Typography>
                                                    </Box>

                                                    {/* <Button
                                                        component={Link}
                                                        to={project.link}
                                                        color="primary"
                                                        variant="contained"
                                                        endIcon={<ArrowRightAlt />}
                                                        sx={{
                                                            fontWeight: 700,
                                                            borderRadius: 2,
                                                            px: 3,
                                                            py: 1,
                                                            background: `linear-gradient(90deg, ${project.accentColor || theme.palette.primary.main}, ${project.secondaryColor || theme.palette.secondary.main})`,
                                                            boxShadow: `0 4px 12px ${alpha(project.accentColor || theme.palette.primary.main, 0.3)}`,
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: `0 6px 16px ${alpha(project.accentColor || theme.palette.primary.main, 0.4)}`
                                                            },
                                                            transition: 'all 0.3s ease',
                                                            minWidth: 120
                                                        }}
                                                        size={isMobile ? 'small' : 'medium'}
                                                    >
                                                        Explore
                                                    </Button> */}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                </Grid>
                            ))}
                        </Grid>
                    </motion.div>

                    {/* CTA Section */}
                    <Box sx={{
                        textAlign: 'center',
                        mt: { xs: 8, md: 12 },
                        position: 'relative'
                    }}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <Typography variant="h5" component="p" sx={{
                                mb: 3,
                                fontWeight: 600,
                                color: 'text.secondary'
                            }}>
                                Ready to start your project?
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="large"
                                endIcon={<RocketLaunch />}
                                sx={{
                                    px: 6,
                                    py: 1.5,
                                    borderRadius: 3,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    textTransform: 'none',
                                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.4)}`,
                                        background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                                onClick={() => navigate('/contact')}
                            >
                                Let's Build Something Amazing
                            </Button>
                        </motion.div>
                    </Box>
                </Container>
            </Box>

            {/* Testimonials Section with Slick Carousel */}
            {/*<Box id="testimonials" sx={{
                py: { xs: 8, md: 12 },
                bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '50%',
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(to bottom, rgba(26, 26, 46, 0.9), rgba(26, 26, 46, 0))'
                        : 'linear-gradient(to bottom, rgba(245, 247, 255, 0.9), rgba(245, 247, 255, 0))',
                    zIndex: 0
                }
            }}>
                 <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                        textAlign: 'center',
                        mb: { xs: 6, md: 10 },
                        position: 'relative'
                    }}>
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.8 }}
                        >
                            <Chip
                                label="CLIENT TESTIMONIALS"
                                color="primary"
                                sx={{
                                    mb: 3,
                                    fontWeight: 700,
                                    letterSpacing: 2,
                                    px: 3,
                                    py: 1,
                                    fontSize: '0.75rem',
                                    backdropFilter: 'blur(10px)',
                                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    color: theme.palette.primary.main,
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    textTransform: 'uppercase'
                                }}
                            />
                            <Typography
                                variant={isMobile ? 'h3' : 'h2'}
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontWeight: 800,
                                    letterSpacing: '-0.03em',
                                    color: theme.palette.text.primary,
                                    mb: 2,
                                    lineHeight: 1.2
                                }}
                            >
                                What Our <Box component="span" sx={{ color: theme.palette.primary.main }}>Clients Say</Box>
                            </Typography>
                            <Typography
                                variant={isMobile ? 'body1' : 'h6'}
                                color="text.secondary"
                                sx={{
                                    maxWidth: 700,
                                    mx: 'auto',
                                    lineHeight: 1.6,
                                    fontSize: isMobile ? '1rem' : '1.25rem',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -20,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 80,
                                        height: 4,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        borderRadius: 2
                                    }
                                }}
                            >
                                Hear from our satisfied clients about their experiences working with us
                            </Typography>
                        </motion.div>
                    </Box>

                    <Box sx={{
                        maxWidth: { xs: '100%', md: '1200px' },
                        mx: 'auto',
                        px: { xs: 0, md: 2 }
                    }}>
                        <Slider {...sliderSettings}>
                            {testimonials.map((testimonial, index) => (
                                <Box key={index} sx={{ px: { xs: 1, md: 2 }, py: 1 }}>
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            p: 4,
                                            height: 280, // Fixed height for consistency
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between', // Helps with layout distribution
                                            borderRadius: 3,
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.background.paper, 0.8)
                                                : alpha(colors.primary, 0.03),
                                            border: `1px solid ${alpha(
                                                theme.palette.mode === 'dark'
                                                    ? theme.palette.primary.light
                                                    : colors.primary,
                                                0.1
                                            )}`,
                                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-5px)',
                                                boxShadow: theme.palette.mode === 'dark'
                                                    ? `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`
                                                    : `0 10px 20px ${alpha(colors.primary, 0.1)}`
                                            },
                                            mx: 1
                                        }}
                                    >
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ mb: 1 }}>
                                                {renderRatingStars(testimonial.rating)}
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                color={theme.palette.mode === 'dark' ? theme.palette.common.white : colors.textPrimary}
                                                paragraph
                                                sx={{
                                                    mb: 'auto', // Push content up, footer down
                                                    fontStyle: 'italic',
                                                    fontSize: '1.1rem',
                                                    lineHeight: 1.6,
                                                    overflow: 'hidden',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 5,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                "{testimonial.comment}"
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Divider sx={{
                                                my: 2,
                                                borderColor: theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.divider, 0.5)
                                                    : alpha(theme.palette.divider, 0.3)
                                            }} />

                                            <Box  sx={{mt: 0, mb: 0,pt: 0,pb: 0, display: 'flex', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography
                                                        variant="h6"
                                                        component="div"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: theme.palette.mode === 'dark'
                                                                ? theme.palette.common.white
                                                                : colors.textPrimary
                                                        }}
                                                    >
                                                        {testimonial.name}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ ml: 'auto', display: { xs: 'none', sm: 'block' } }}>
                                                    <Box
                                                        component="img"
                                                        src={testimonial.companyLogo}
                                                        alt="Company logo"
                                                        sx={{
                                                            height: 'auto',
                                                            maxWidth: 80,
                                                            maxHeight: 80,
                                                            filter: theme.palette.mode === 'dark'
                                                                ? 'brightness(0) invert(1)'
                                                                : 'none',
                                                            opacity: 0.8
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Box>
                            ))}
                        </Slider>
                    </Box>
                </Container> 



            </Box>*/}
            {/* Final CTA Section */}
            <Box sx={{
                py: { xs: 8, md: 12 },
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(10, 10, 26, 0.9) 0%, rgba(26, 26, 46, 0.9) 100%)'
                    : 'linear-gradient(135deg, rgba(245, 247, 255, 0.9) 0%, rgba(230, 235, 255, 0.9) 100%)',
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
                <Container maxWidth="md">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                    >
                        <Box sx={{
                            textAlign: 'center',
                            p: { xs: 3, md: 6 },
                            borderRadius: 3,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: theme.palette.mode === 'dark'
                                    ? 'radial-gradient(circle at 100% 50%, rgba(106, 27, 255, 0.1) 50%, transparent 100%)'
                                    : 'radial-gradient(circle at 100% 50%, rgba(94, 0, 255, 0.05) 50%, transparent 100%)',
                                zIndex: 0,
                            },
                        }}>
                            <motion.div variants={fadeInUp}>
                                <Typography
                                    variant={isMobile ? 'h4' : 'h3'}
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 700,
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    Ready to Transform Your Business?
                                </Typography>
                            </motion.div>
                            <motion.div variants={fadeInUp}>
                                <Typography
                                    variant={isMobile ? 'body1' : 'h6'}
                                    color="text.secondary"
                                    paragraph
                                    sx={{
                                        mb: 4,
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    Let's discuss how we can help you achieve your digital goals.
                                </Typography>
                            </motion.div>
                            <motion.div variants={fadeInUp}>
                                <Button
                                    variant="contained"
                                    size={isMobile ? 'large' : 'large'}
                                    endIcon={<ArrowRightAlt />}
                                    onClick={() => navigate('/contact')}
                                    sx={{
                                        px: 6,
                                        py: 1.5,
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: theme.shadows[4],
                                        '&:hover': {
                                            boxShadow: theme.shadows[8],
                                        },
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    Get Started
                                </Button>
                            </motion.div>
                        </Box>
                    </motion.div>
                </Container>
            </Box>

            {/* Video Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleVideoClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible'
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <IconButton
                        onClick={handleVideoClose}
                        sx={{
                            position: 'absolute',
                            top: -40,
                            right: -40,
                            color: 'common.white',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.7)'
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                    {selectedVideo && (
                        <iframe
                            width="100%"
                            height="500"
                            src={selectedVideo}
                            title="Project Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ borderRadius: 8 }}
                        ></iframe>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Home;